import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../ui/utils";

import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Breadcrumb } from "../Breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Plus, Edit, Trash2, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import API from "../api/api01";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  address?: string;
  website?: string;
  login_id: string;
  status: "Active" | "Inactive";
  group_id?: string | null;
  group_name?: string;
}

import { useNavigate } from "react-router-dom";
import { PATHS } from "../config/paths";
import { isValidEmail, isValidPhone, filterNumeric } from "../utils/validation";

interface ManageClientProps { }

export function ManageClient({ }: ManageClientProps) {
  const navigate = useNavigate();

  const [clients, setClients] = useState<Client[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [statusPendingClient, setStatusPendingClient] = useState<Client | null>(null);

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    login_id: "",
    password: "",
    website: "",
  });

  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<{ id: string, name: string }[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [permissions, setPermissions] = useState({
    view: true,
    insert: false,
    update: false,
    delete: false
  });

  const fetchClients = async () => {
    try {
      const { data } = await API.get("/superadmin/clients");
      setClients(data);
      if (data && data.length > 0) {
        toast.success("Clients loaded successfully");
      } else {
        toast.error("No records found");
      }
    } catch (err) {
      toast.error("Failed to fetch clients");
    }
  };

  useEffect(() => {
    fetchClients();
    fetchGroups();
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const { data } = await API.get("/superadmin/module-permissions/manage-client");
      setPermissions(data);
    } catch (error) {
      console.error("Failed to fetch permissions", error);
    }
  };

  const fetchGroups = async () => {
    try {
      const { data } = await API.get("/superadmin/groups");
      setGroups(data.filter((g: any) => g.group_type === 'client'));
    } catch (err) {
      toast.error("Failed to fetch groups");
    }
  };

  const filteredClients = clients.filter(
    (client) =>
      client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.login_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openEditDialog = (client: Client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name || "",
      email: client.email || "",
      phone: client.phone || "",
      address: client.address || "",
      login_id: client.login_id || "",
      password: "",
      website: client.website || "",
    });
    setSelectedGroupId(client.group_id || "no-change");
    setIsEditDialogOpen(true);
  };


  const openDeleteDialog = (client: Client) => {
    setSelectedClient(client);
    setDeleteDialogOpen(true);
  };

  const handleEdit = async () => {
    if (!selectedClient) return;

    if (formData.email && !isValidEmail(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (formData.phone && !isValidPhone(formData.phone)) {
      toast.error("Please enter a valid mobile number (10-15 digits)");
      return;
    }

    setLoading(true);
    try {
      let permissions = undefined;
      let groupIdToSend = undefined;
      if (selectedGroupId === "no-group") {
        groupIdToSend = null;
      } else if (selectedGroupId !== "no-change" && selectedGroupId !== selectedClient.group_id) {
        groupIdToSend = selectedGroupId;
      }

      if (selectedGroupId && selectedGroupId !== "no-change" && selectedGroupId !== "no-group") {
        const permRes = await API.get(`/superadmin/group-permissions/${selectedGroupId}`);
        permissions = JSON.stringify(permRes.data);
      }

      const dataToSend: any = { ...formData };
      
      // Don't send empty password
      if (!dataToSend.password) delete dataToSend.password;
      
      await API.put(`/superadmin/clients/${selectedClient.id}`, {
        ...dataToSend,
        group_id: groupIdToSend,
        permissions
      });

      toast.success("Client updated successfully");
      setIsEditDialogOpen(false);
      fetchClients();
    } catch (err) {
      toast.error("Failed to update client");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedClient) return;

    setLoading(true);
    try {
      await API.delete(`/superadmin/clients/${selectedClient.id}`);
      toast.success("Client deleted successfully");
      setDeleteDialogOpen(false);
      fetchClients();
    } catch (err) {
      toast.error("Failed to delete client");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (client: Client) => {
    setStatusPendingClient(client);
    setStatusConfirmOpen(true);
  };

  const confirmStatusToggle = async () => {
    if (!statusPendingClient) return;
    const client = statusPendingClient;
    const newStatus = client.status === "Active" ? "Inactive" : "Active";
    setLoading(true);
    try {
      await API.patch(`/superadmin/clients/${client.id}/status`, { status: newStatus });
      toast.success(`Client ${newStatus === "Active" ? "activated" : "deactivated"} successfully`);
      fetchClients();
    } catch (err) {
      toast.error("Failed to update client status");
    } finally {
      setLoading(false);
      setStatusConfirmOpen(false);
      setStatusPendingClient(null);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">

      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}

        <div className="flex items-center justify-between">

          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">
              Manage Client
            </h1>


            <Breadcrumb
              items={[{ label: "Manage Client" }, { label: "All Clients" }]}
              className="mt-2"
            />
          </div>



          <Button
            onClick={() => navigate(PATHS.ADD_CLIENT)}
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm h-10 px-6 rounded-lg transition-all active:scale-95"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="text-[13px] font-bold uppercase tracking-widest text-white">Add Client</span>
          </Button>


        </div>


        {/* Search */}

        <Card className="shadow-sm border border-slate-200 dark:border-slate-800 bg-card">
          <CardContent className="p-4">

            <div className="relative">

              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

              <Input
                placeholder="Search by name, email, or login ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-background border-slate-200 dark:border-slate-800"
              />

            </div>

          </CardContent>
        </Card>


        {/* Table */}

        <Card className="shadow-sm border border-slate-200 dark:border-slate-800 bg-card">

          <CardHeader className="pb-4 border-b border-slate-100 dark:border-slate-800/60">
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground/80">
              Clients ({filteredClients.length})
            </CardTitle>

          </CardHeader>

          <CardContent>

            <div className="overflow-x-auto rounded-md border border-white/5">

              <Table>

                <TableHeader className="bg-muted/30">

                  <TableRow className="hover:bg-transparent border-slate-200 dark:border-slate-800">
                    <TableHead className="text-xs uppercase font-bold tracking-widest text-muted-foreground/70 py-4 pl-6">Client</TableHead>
                    <TableHead className="text-xs uppercase font-bold tracking-widest text-muted-foreground/70 py-4">Email</TableHead>
                    <TableHead className="text-xs uppercase font-bold tracking-widest text-muted-foreground/70 py-4">Phone</TableHead>
                    <TableHead className="text-xs uppercase font-bold tracking-widest text-muted-foreground/70 py-4">Login ID</TableHead>
                    <TableHead className="text-xs uppercase font-bold tracking-widest text-muted-foreground/70 py-4">Status</TableHead>
                    <TableHead className="text-xs uppercase font-bold tracking-widest text-muted-foreground/70 py-4 text-right pr-6">Actions</TableHead>
                  </TableRow>


                </TableHeader>



                <TableBody>

                  {filteredClients.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-20 text-muted-foreground group">
                        <div className="flex flex-col items-center gap-3 animate-in fade-in zoom-in-95 duration-500">
                          <div className="h-16 w-16 rounded-full bg-slate-100 dark:bg-slate-800/50 flex items-center justify-center text-slate-400 group-hover:scale-110 transition-transform">
                            <Search className="h-8 w-8" />
                          </div>
                          <div className="space-y-1">
                            <p className="text-lg font-semibold text-slate-900 dark:text-white">No clients found</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Try adjusting your search or{" "}
                              <button 
                                onClick={() => navigate(PATHS.ADD_CLIENT)}
                                className="text-blue-600 hover:underline font-bold"
                              >
                                add a new client
                              </button>
                              {" "}to get started.
                            </p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}

                  {filteredClients.map((client) => (

                    <TableRow
                      key={client.id}
                      className="hover:bg-background/50 transition-colors border-white/5"
                    >

                      {/* Client */}

                      <TableCell>

                        <div className="flex items-center gap-3">

                          <div className="h-9 w-9 rounded-full bg-blue-50 dark:bg-blue-900/40 flex items-center justify-center font-bold text-blue-600 dark:text-blue-400 text-xs">
                            {client.name.substring(0, 2).toUpperCase()}
                          </div>


                          <div className="leading-tight">
                            <p className="text-[13px] font-bold text-slate-900 dark:text-white">{client.name}</p>
                            <p className="text-[11px] font-medium text-muted-foreground">
                              {client.login_id}
                            </p>
                          </div>


                        </div>

                      </TableCell>


                      <TableCell className="text-[13px] text-muted-foreground font-medium">
                        {client.email}
                      </TableCell>

                      <TableCell className="text-[13px] font-medium text-slate-600 dark:text-slate-400">{client.phone}</TableCell>

                      <TableCell className="text-[13px] font-bold tracking-tight text-blue-600 dark:text-blue-400">
                        {client.login_id}
                      </TableCell>



                      {/* Status */}

                      <TableCell>

                        <div className="flex items-center gap-3">

                          <Switch
                            checked={client.status === "Active"}
                            onCheckedChange={() => handleStatusToggle(client)}
                          />

                          <Badge
                            className={cn(
                              "text-[10px] font-bold uppercase tracking-widest px-2.5 py-0.5",
                              client.status === "Active"
                                ? "bg-emerald-500/10 text-emerald-600 border-none shadow-none"
                                : "bg-red-500/10 text-red-600 border-none shadow-none"
                            )}
                          >
                            {client.status}
                          </Badge>


                        </div>

                      </TableCell>


                      {/* Actions */}

                      <TableCell className="text-right">

                        <div className="flex justify-end gap-1">

                          <Button
                            size="icon"
                            variant="ghost"
                            className="hover:bg-blue-100 hover:text-blue-600 disabled:opacity-30"
                            onClick={() => openEditDialog(client)}
                            disabled={!permissions.update}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>

                          <Button
                            size="icon"
                            variant="ghost"
                            className="hover:bg-red-100 hover:text-red-600 disabled:opacity-30"
                            onClick={() => openDeleteDialog(client)}
                            disabled={!permissions.delete}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>

                        </div>

                      </TableCell>

                    </TableRow>

                  ))}

                </TableBody>

              </Table>

            </div>
          </CardContent>
        </Card>
      </div>

      {/* EDIT DIALOG */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-card shadow-xl border border-slate-200 dark:border-slate-800 max-w-2xl rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight">Edit Client</DialogTitle>
          </DialogHeader>


          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2 md:col-span-2 pb-2 border-b border-slate-100 dark:border-slate-800">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Update Group (Permissions)</Label>

              <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                <SelectTrigger className="border-slate-200 dark:border-slate-800">
                  <SelectValue placeholder="Select a Group to reset permissions" />
                </SelectTrigger>
                <SelectContent className="border-slate-200 dark:border-slate-800">
                  <SelectItem value="no-change">Don't Change Group</SelectItem>
                  <SelectItem value="no-group">None (Remove from Group)</SelectItem>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">Selecting a group will override current client permissions.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Full Name</Label>

              <Input
                placeholder={selectedClient?.name}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="border-slate-200 dark:border-slate-800"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Email</Label>

              <Input
                type="email"
                placeholder={selectedClient?.email}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="border-slate-200 dark:border-slate-800"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Phone Number</Label>

              <Input
                placeholder={selectedClient?.phone}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: filterNumeric(e.target.value) })}
                className="border-slate-200 dark:border-slate-800"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Login ID</Label>

              <Input
                placeholder={selectedClient?.login_id}
                value={formData.login_id}
                onChange={(e) => setFormData({ ...formData, login_id: e.target.value })}
                className="border-slate-200 dark:border-slate-800"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Address</Label>
              <Input
                placeholder="Enter address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="border-slate-200 dark:border-slate-800"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Website</Label>
              <Input
                placeholder="https://example.com"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="border-slate-200 dark:border-slate-800"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Update Password (Leave blank to keep current)</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="border-slate-200 dark:border-slate-800"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} disabled={loading} className="bg-blue-600 text-white">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRMATION */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border border-border shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold tracking-tight text-red-600">Are you absolutely sure?</AlertDialogTitle>

            <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
              This action cannot be undone. This will permanently delete the client
              <span className="font-semibold text-slate-900 dark:text-white mx-1">
                {selectedClient?.name}
              </span>
              and remove their data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200 dark:border-slate-800">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete Client"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* STATUS CONFIRMATION */}
      <AlertDialog open={statusConfirmOpen} onOpenChange={setStatusConfirmOpen}>
        <AlertDialogContent className="bg-card border border-border shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className={cn(
              "text-lg font-bold tracking-tight",
              statusPendingClient?.status === "Active" ? "text-amber-600" : "text-emerald-600"
            )}>
              {statusPendingClient?.status === "Active" ? "Deactivate Client?" : "Activate Client?"}
            </AlertDialogTitle>

            <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
              Are you sure you want to {statusPendingClient?.status === "Active" ? "deactivate" : "activate"} 
              <span className="font-semibold text-slate-900 dark:text-white mx-1">
                {statusPendingClient?.name}
              </span>?
              {statusPendingClient?.status === "Active" 
                ? " The user will no longer be able to log in to the system." 
                : " The user will regain access to the system."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmStatusToggle();
              }}
              className={cn(
                "text-white",
                statusPendingClient?.status === "Active" ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"
              )}
              disabled={loading}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm {statusPendingClient?.status === "Active" ? "Deactivation" : "Activation"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}